import React from "react";
import { PartOutlineImage } from "../../utils/packagingOutline.jsx";
import { PalletScanChip } from "./KioskScanner";
import { SurplusCratesContext } from "../SurplusRack";
import {
  assemblyNote, beaconLine, destinationsInFillOrder, doNotAddReason, holdingDestinationLabel, physicalPalletName,
  pieceTotals, ridesOnHost, shownFillOrder,
} from "../../features/laserKiosk/kioskRules";

// Laser packaging kiosk pieces shared by the per-part view and the all-parts view.
// Dark kiosk palette, sized to be read from across the laser bay.

// A surplus tile's place: the SURPLUS RACK, or a SURPLUS CRATE while crates are on (Mike 2026-11-04).
function SurplusTileWhere() {
  const cratesOn = React.useContext(SurplusCratesContext);
  return <span style={{ fontWeight: 800, color: "#ffeeb0" }}>{cratesOn ? "SURPLUS CRATE" : "SURPLUS RACK"}</span>;
}

// "1 of 3": the order to fill a part's destinations in.
export function FillOrderChip({ order, size = "big" }) {
  if (!order) return null;
  const big = size === "big";
  return (
    <span
      data-testid="fill-order"
      title="Fill the destinations in this order"
      style={{
        display: "inline-flex", alignItems: "baseline", gap: "0.3rem",
        background: "#f0f2f5", color: "#0f1115", borderRadius: 999,
        padding: big ? "0.15rem 0.85rem" : "0.05rem 0.55rem",
        fontWeight: 800, fontFamily: "monospace", whiteSpace: "nowrap",
        fontSize: big ? "1.6rem" : "1.1rem", lineHeight: 1.2,
      }}
    >
      {order.seq}
      <span style={{ fontSize: big ? "1rem" : "0.8rem", fontWeight: 700 }}>of {order.of}</span>
    </span>
  );
}

// The beacon on the pallet ("BCN-012", flashing or not), or that it has none.
export function BeaconChip({ dest, size = "big" }) {
  const b = beaconLine(dest);
  if (!b) return null;
  const big = size === "big";
  if (b.tone === "none") {
    return (
      <span data-testid="pallet-beacon" style={{ fontSize: big ? "0.95rem" : "0.85rem", color: "#8fa3b8", fontStyle: "italic" }}>
        {b.text}
      </span>
    );
  }
  const lit = b.tone === "lit";
  return (
    <span
      data-testid="pallet-beacon"
      title={lit ? "This pallet's beacon is flashing" : "The beacon on this pallet"}
      style={{
        display: "inline-flex", alignItems: "center", gap: "0.4rem",
        background: lit ? "#12422a" : "#1c2a3a", border: `2px solid ${lit ? "#3ddc84" : "#4ec9ff"}`,
        color: lit ? "#9ff0c2" : "#bfe6ff", borderRadius: 8, padding: big ? "0.15rem 0.6rem" : "0.05rem 0.45rem",
        fontFamily: "monospace", fontWeight: 800, fontSize: big ? "1.3rem" : "1rem", whiteSpace: "nowrap",
      }}
    >
      <span aria-hidden="true" style={{
        width: big ? 12 : 9, height: big ? 12 : 9, borderRadius: "50%",
        background: lit ? "#3ddc84" : "#4ec9ff",
      }} />
      {b.text}
      {lit && <span style={{ fontSize: big ? "0.85rem" : "0.75rem", fontFamily: "sans-serif" }}>FLASHING</span>}
    </span>
  );
}

// The red DO NOT ADD block at the top of a pallet card (or tile) whose pallet is closed.
export function DoNotAddBanner({ reason, qty, compact = false }) {
  if (!reason) return null;
  return (
    <div
      data-testid="do-not-add"
      role="alert"
      style={{
        background: "#b00020", color: "white", borderRadius: 8,
        padding: compact ? "0.35rem 0.6rem" : "0.6rem 0.9rem",
        display: "flex", flexDirection: "column", gap: "0.15rem",
      }}
    >
      <div style={{ fontSize: compact ? "1.3rem" : "2rem", fontWeight: 900, letterSpacing: "0.06em", lineHeight: 1.05 }}>
        ⛔ DO NOT ADD
      </div>
      <div style={{ fontSize: compact ? "0.95rem" : "1.2rem", fontWeight: 800 }}>{reason}</div>
      {!compact && (
        <div style={{ fontSize: "1.05rem", fontWeight: 600 }}>
          Set {qty != null ? `these ${qty} pcs` : "these pieces"} aside and tell the office.
        </div>
      )}
    </div>
  );
}

// ─── All parts on the sheet, unloaded together ─────────────────────────
// One row per part: the part, then its destinations in fill order. Click a row (or its
// "Details" button) for the part's full view with pallet maps.
const TILE_STYLE = {
  packaging: { bg: "#14321d", border: "#2d7a3e" },
  owed: { bg: "#3a1c1a", border: "#c0392b" },
  surplus: { bg: "#3a3320", border: "#a8882b" },
  blocked: { bg: "#3a0a12", border: "#ff3b4e" },
};

function DestinationTile({ dest, order }) {
  const blocked = doNotAddReason(dest);
  const tone = blocked ? "blocked" : dest.kind;
  const st = TILE_STYLE[tone] || TILE_STYLE.packaging;
  const container = dest.is_pallet_direct ? "STACK" : "BOX";
  return (
    <div
      data-testid="unload-tile"
      style={{
        background: st.bg, border: `3px solid ${st.border}`, borderRadius: 10,
        padding: "0.35rem 0.6rem", minWidth: 210, flex: "0 1 270px",
        display: "flex", flexDirection: "column", gap: "0.2rem",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "0.45rem", flexWrap: "wrap" }}>
        <FillOrderChip order={order} size="small" />
        {dest.kind === "packaging" && (
          <span style={{ fontFamily: "monospace", fontWeight: 800, fontSize: "1.2rem", color: "#e6c200" }}>
            {physicalPalletName(dest)}
          </span>
        )}
        {dest.kind === "owed" && (
          <span style={{ fontWeight: 800, color: "#ffb3a7" }}>
            {dest.owed_kind === "bad_idea" ? "BAD IDEA STOCK" : "NOT SURPLUS"}
          </span>
        )}
        {dest.kind === "surplus" && <SurplusTileWhere />}
        {dest.dest_so_number && (
          <span style={{ fontFamily: "monospace", color: "#4ec9ff", fontWeight: 700 }}>{dest.dest_so_number}</span>
        )}
      </div>
      {holdingDestinationLabel(dest) && (
        <div data-testid="holding-tile-label" style={{ color: "#ffd969", fontWeight: 800, fontSize: "0.9rem" }}>
          {holdingDestinationLabel(dest)}
        </div>
      )}
      {ridesOnHost(dest) && (
        <div style={{ color: "#4ec9ff", fontWeight: 700, fontSize: "0.9rem" }}>
          ↪ rides on {dest.riding_to_so_number || "another order"}'s pallet
        </div>
      )}
      {blocked && <DoNotAddBanner reason={blocked} qty={dest.qty} compact />}
      {dest.kind === "packaging" && (
        <div style={{ display: "flex", alignItems: "center", gap: "0.45rem", flexWrap: "wrap", color: "#bcd4ff" }}>
          <BeaconChip dest={dest} size="small" />
          <PalletScanChip dest={dest} size="small" />
          <span style={{ fontFamily: "monospace", fontWeight: 700 }}>
            {container} {dest.box_short_id || `${dest.is_pallet_direct ? "S" : "B"}${dest.box_num}`}
          </span>
          {dest.box_sku && <span>{dest.box_sku}</span>}
        </div>
      )}
      {dest.kind === "owed" && dest.owed_kind !== "bad_idea" && (
        <div style={{ color: "#ffd2c9", fontWeight: 700 }}>Set aside, tell the office</div>
      )}
      <div style={{ display: "flex", alignItems: "baseline", gap: "0.35rem" }}>
        <span style={{ fontSize: "2.4rem", fontWeight: 800, fontFamily: "monospace", color: "white", lineHeight: 1 }}>
          {dest.qty}
        </span>
        <span style={{ fontSize: "1.1rem", color: "#c9d4e2", fontWeight: 700 }}>pcs</span>
      </div>
    </div>
  );
}

function PartThumb({ part, outlineCache }) {
  const outline = outlineCache && part.outline_svg_path ? outlineCache[part.outline_svg_path] : null;
  const hasOutline = !!(outline && outline.paths && outline.paths.length);
  return (
    <div style={{
      width: 84, height: 84, flexShrink: 0, borderRadius: 8, overflow: "hidden",
      background: hasOutline || part.image_path ? "#fafafa" : "#2a2f3a",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {hasOutline ? (
        <PartOutlineImage data={outline} />
      ) : part.image_path ? (
        <img src={part.image_path} alt={part.product_id}
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
          onError={(e) => { e.currentTarget.style.display = "none"; }} />
      ) : (
        <span style={{ color: "#6c757d", fontSize: "0.85rem" }}>(no image)</span>
      )}
    </div>
  );
}

export function AllPartsUnload({ parts, outlineCache, onOpenPart }) {
  return (
    <div
      data-testid="all-parts-unload"
      style={{ display: "flex", flexDirection: "column", gap: "0.6rem", flex: 1, minHeight: 0, overflowY: "auto" }}
    >
      {(parts || []).map((part, idx) => {
        const order = shownFillOrder(part.destinations);
        const dests = destinationsInFillOrder(part.destinations);
        const totals = pieceTotals(part);
        const isAssembly = !!(part.assembly_id && part.assembly_name);
        const note = assemblyNote(part.qty_ordered, part);
        return (
          <div
            key={part.part_id}
            data-testid="unload-part-row"
            style={{
              display: "flex", gap: "0.8rem", alignItems: "stretch",
              background: "#1a1d24", border: "2px solid #2a2f3a", borderRadius: 12, padding: "0.45rem 0.6rem",
            }}
          >
            <div style={{ display: "flex", gap: "0.6rem", width: 360, flexShrink: 0 }}>
              <PartThumb part={part} outlineCache={outlineCache} />
              <div style={{ display: "flex", flexDirection: "column", gap: "0.1rem", minWidth: 0 }}>
                <div style={{ color: "#9aa4b4", fontWeight: 800, fontSize: "0.9rem" }}>
                  PART {idx + 1} OF {parts.length}
                  {isAssembly && <span style={{ marginLeft: 8, color: "#7ede9e" }}>ASSEMBLY</span>}
                </div>
                <div style={{ color: "white", fontWeight: 800, fontSize: "1.35rem", fontFamily: isAssembly ? "inherit" : "monospace", lineHeight: 1.1, wordBreak: "break-word" }}>
                  {isAssembly ? part.assembly_name : part.product_id}
                </div>
                {isAssembly && (
                  <div style={{ color: "#bcd4ff", fontFamily: "monospace", fontSize: "0.95rem" }}>{part.product_id}</div>
                )}
                <div style={{ color: "#e8f2ea", fontSize: "1.05rem" }}>
                  <b>{part.qty_ordered}</b> pcs on this job
                  {note && <span style={{ color: "#9aa4b4" }}> {note}</span>}
                </div>
                {(totals.owed > 0 || totals.surplus > 0 || totals.blocked > 0) && (
                  <div style={{ color: "#c9d4e2", fontSize: "0.9rem" }}>
                    {[totals.packaging && `${totals.packaging} on pallets`,
                      totals.owed && `${totals.owed} owed`,
                      totals.surplus && `${totals.surplus} surplus`,
                      totals.blocked && `${totals.blocked} DO NOT ADD`].filter(Boolean).join(" · ")}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => onOpenPart && onOpenPart(part.part_id)}
                  style={{
                    marginTop: "auto", alignSelf: "flex-start", background: "#2a2f3a", color: "#bcd4ff",
                    border: "2px solid #3a4050", borderRadius: 8, padding: "0.1rem 0.6rem",
                    fontWeight: 700, fontSize: "0.85rem", cursor: "pointer",
                  }}
                >
                  Details + pallet map →
                </button>
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", alignContent: "flex-start", flex: 1, minWidth: 0 }}>
              {dests.length === 0 ? (
                <div style={{ color: part.next_station ? "#ffd969" : "#8fa3b8", fontSize: "1.2rem", fontWeight: 700, alignSelf: "center" }}>
                  {part.next_station
                    ? `ROUTE TO ${String(part.next_station).toUpperCase()} — stage on the WIP rack`
                    : "No destinations routed for this part yet."}
                </div>
              ) : dests.map((d, i) => (
                <DestinationTile key={`${d.kind}-${d.box_id || i}`} dest={d} order={order.get(d)} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
